'use client'

import React, { useMemo, useState } from 'react'
import { Upload as UploadIcon } from 'lucide-react'
import type { FieldErrorsImpl, UseFormRegister } from 'react-hook-form'

import { Error } from '@/blocks/Form/Error'

type UploadFieldProps = {
  name: string
  label?: string | null
  description?: string | null
  required?: boolean | null
  allowMultiple?: boolean | null
  maxFiles?: number | null
  errors: Partial<FieldErrorsImpl>
  register: UseFormRegister<any>
}

const ACCEPTED_MIME_TYPES = ['application/pdf']
const MAX_FILES_DEFAULT = 5
const MAX_FILE_SIZE = 2 * 1024 * 1024 // 2MB in bytes

export const UploadField: React.FC<UploadFieldProps> = ({
  name,
  label,
  description,
  required,
  allowMultiple,
  maxFiles,
  errors,
  register,
}) => {
  const [fileNames, setFileNames] = useState<string[]>([])

  const maxAllowed = useMemo(() => {
    const limit = maxFiles ?? MAX_FILES_DEFAULT
    return Math.min(limit, MAX_FILES_DEFAULT)
  }, [maxFiles])

  const { ref, onChange, ...registeredProps } = register(name, {
    required: required ? 'Dieses Feld ist erforderlich.' : false,
    validate: (value: FileList) => {
      const files = Array.from(value || [])

      if (!allowMultiple && files.length > 1) {
        return 'Es ist nur eine Datei erlaubt.'
      }

      if (files.length === 0) {
        return required ? 'Bitte eine PDF hochladen.' : true
      }

      if (files.length > maxAllowed) {
        return `Maximal ${maxAllowed} PDF-Dateien erlaubt.`
      }

      const invalidType = files.find((file) => !ACCEPTED_MIME_TYPES.includes(file.type))
      if (invalidType) {
        return 'Nur PDF-Dateien sind erlaubt.'
      }

      const oversizedFile = files.find((file) => file.size > MAX_FILE_SIZE)
      if (oversizedFile) {
        return `Datei "${oversizedFile.name}" ist zu groß. Maximale Dateigröße: 2MB.`
      }

      return true
    },
  })

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const files = Array.from(event.target.files || [])
    setFileNames(files.map((file) => file.name))
    onChange(event)
  }

  const errorMessage = errors?.[name]

  return (
    <div className="space-y-2">
      {label && (
        <p className="text-base md:text-lg text-text-secondary">
          {label}
          {required && <span className="text-text-primary"> *</span>}
        </p>
      )}
      <label className="flex items-center justify-between gap-3 border border-dashed border-[color:var(--color-border-brand)]/30 px-4 py-3 cursor-pointer">
        <span className="text-text-secondary">.pdf</span>
        <UploadIcon className="h-4 w-4 text-text-brand" />
        <input
          id={name}
          type="file"
          accept=".pdf"
          multiple={Boolean(allowMultiple)}
          ref={ref}
          onChange={handleChange}
          className="hidden"
          {...registeredProps}
        />
      </label>
      {description && <p className="text-sm text-text-secondary">{description}</p>}
      {fileNames.length > 0 && (
        <ul className="text-sm text-text-secondary list-disc list-inside">
          {fileNames.map((fileName) => (
            <li key={fileName}>{fileName}</li>
          ))}
        </ul>
      )}
      {errorMessage && <Error name={name} />}
    </div>
  )
}
